import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Text, Select, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, Table, Thead, Tbody, Tr, Th, Td,
  Input, FormControl, FormLabel, FormHelperText, Badge, Box, Heading,
  Spinner, Flex, CloseButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { apiFetch } from './api';
import { reactSelectStyles } from './selectStyles';

const API_BASE = 'https://n441mm4tfl.execute-api.eu-west-2.amazonaws.com/prod';
const APIS_API = `${API_BASE}/listecrrepos`;

type ApiKeyItem = {
  apiKeyHash: string;
  username: string;
  allowedApis: string;
  createdAt: string;
  expiresAt: string;
  email: string;
};

type OptionType = { value: string; label: string };
type ApiRule = { repo: string; subPath: string; methods: string[] };

function mergeRules(rules: ApiRule[]): ApiRule[] {
  const grouped: Record<string, { repo: string; subPaths: string[]; methods: string[] }> = {};
  rules.forEach(rule => {
    if (!grouped[rule.repo]) {
      grouped[rule.repo] = { repo: rule.repo, subPaths: [], methods: [] };
    }
    if (rule.subPath && rule.subPath.trim() !== '') {
      const subParts = rule.subPath.split(',').map(s => s.trim()).filter(Boolean);
      grouped[rule.repo].subPaths.push(...subParts);
    }
    grouped[rule.repo].methods.push(...rule.methods);
  });
  return Object.values(grouped).map(g => ({
    repo: g.repo,
    subPath: Array.from(new Set(g.subPaths)).join(', '),
    methods: Array.from(new Set(g.methods)),
  }));
}

function cleanPathRegex(raw: string): string {
  let cleaned = raw.replace(/\\/g, '');
  cleaned = cleaned.replace(/^\^/, '').replace(/\$$/, '');
  cleaned = cleaned.replace(
    /\(\[0-9a-f]{8}-\[0-9a-f]{4}-\[0-9a-f]{4}-\[0-9a-f]{4}-\[0-9a-f]{12}\)/gi,
    'uuid'
  );
  cleaned = cleaned.replace(
    /\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)/gi,
    'uuid'
  );
  cleaned = cleaned.replace(/\([^)]*\)/g, 'uuid');
  cleaned = cleaned.replace(/\.\*/g, 'any');
  cleaned = cleaned.replace(/^\/[^/]+/, '');
  if (cleaned === '') cleaned = '/';
  return cleaned;
}

export default function ApiKeyList() {
  const [apiKeys, setApiKeys] = useState<ApiKeyItem[]>([]);
  const [apisState, setApisState] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [pendingDeleteHash, setPendingDeleteHash] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [editingKey, setEditingKey] = useState<ApiKeyItem | null>(null);
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editRules, setEditRules] = useState<ApiRule[]>([]);
  const [editExpiry, setEditExpiry] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const maxDateObj = new Date();
  maxDateObj.setFullYear(maxDateObj.getFullYear() + 10);
  const maxDate = maxDateObj.toISOString().split('T')[0];

  const loadKeys = () => {
    setLoading(true);
    apiFetch(`${API_BASE}/listapikeys`)
      .then(res => res.json())
      .then(data => {
        setApiKeys(data);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadKeys();
    apiFetch(APIS_API)
      .then(res => res.json())
      .then((data: string[]) => setApisState(data))
      .catch(() => setApisState([]));
  }, []);

  const usernames = Array.from(new Set(apiKeys.map(k => k.username)));
  const filteredKeys = selectedUser ? apiKeys.filter(k => k.username === selectedUser) : [];

  const groupedKeys = Object.values(
    filteredKeys.reduce((acc, item) => {
      const key = item.apiKeyHash;
      if (!acc[key]) {
        acc[key] = { ...item, emails: [item.email] };
      } else {
        acc[key].emails.push(item.email);
      }
      return acc;
    }, {} as Record<string, ApiKeyItem & { emails: string[] }>)
  );

  function parseAllowedApis(allowedApis: string | any[]) {
    let apis: any[] = [];
    try {
      apis = Array.isArray(allowedApis) ? allowedApis : JSON.parse(allowedApis);
    } catch {
      apis = [];
    }
    return apis
      .filter(a => a && (typeof a === 'string' || a.path || a.pathRegex))
      .map((a: any) => {
        const raw = typeof a === 'string' ? a : a.path || a.pathRegex || '/';
        const cleaned = cleanPathRegex(raw);
        const methods = typeof a === 'string' ? ['*'] : (a.methods || ['*']);
        return { path: cleaned, methods };
      });
  }

  async function confirmDelete() {
    if (!pendingDeleteHash) return;
    setDeleteError('');
    try {
      const res = await apiFetch(`${API_BASE}/listapikeys/${pendingDeleteHash}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json();
        setDeleteError(err.error || 'Failed to delete API key');
      } else {
        loadKeys();
      }
    } catch {
      setDeleteError('Network error');
    }
    setPendingDeleteHash(null);
  }

  function handleEdit(key: ApiKeyItem) {
    setEditingKey(key);
    setEditError('');
    setEditSaving(false);
    setEditExpiry(key.expiresAt || '');
    try {
      const allowed = JSON.parse(key.allowedApis);
      const rules = allowed.map((api: any) => {
        const raw = api.path || api.pathRegex || '/';
        const cleaned = cleanPathRegex(raw);
        const parts = cleaned.replace(/^\//, '').split('/');
        const repo = parts[0] || '';
        return {
          repo,
          subPath: cleaned.startsWith('/') ? cleaned : '/' + cleaned,
          methods: api.methods || ['*'],
        };
      });
      setEditRules(mergeRules(rules));
    } catch {
      setEditRules([]);
    }
  }

  function handleRuleChange(index: number, field: keyof ApiRule, value: any) {
    const updated = [...editRules];
    (updated[index] as any)[field] = value;
    setEditRules(mergeRules(updated));
  }

  function addRule() {
    setEditRules(mergeRules([...editRules, { repo: '', subPath: '', methods: [] }]));
  }

  function removeRule(index: number) {
    setEditRules(editRules.filter((_, i) => i !== index));
  }

  async function handleEditSave() {
    if (!editingKey) return;
    setEditSaving(true);
    setEditError('');

    if (editExpiry) {
      const exp = new Date(editExpiry);
      const now = new Date();
      if (exp < now) {
        setEditError('Expiry date cannot be in the past');
        setEditSaving(false);
        return;
      }
      if (exp > maxDateObj) {
        setEditError('Expiry date cannot be more than 10 years in the future');
        setEditSaving(false);
        return;
      }
    }

    const merged = mergeRules(editRules);
    const allowedApis = merged.flatMap(r => {
      const subPaths = r.subPath ? r.subPath.split(',').map(s => s.trim()).filter(Boolean) : ['*'];
      return subPaths.map(sp => {
        let path = `/${r.repo}`;
        if (sp !== '*' && sp !== '') {
          path += `/${sp}`;
        } else {
          path += '/*';
        }
        return { path, methods: r.methods.length > 0 ? r.methods : ['*'] };
      });
    });

    const payload = { allowedApis, expiresAt: editExpiry || null };

    try {
      const res = await apiFetch(`${API_BASE}/updateapikeys/${editingKey.apiKeyHash}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        setEditError(err.error || 'Failed to update API key');
      } else {
        setEditingKey(null);
        loadKeys();
      }
    } catch {
      setEditError('Network error');
    }
    setEditSaving(false);
  }

  const methodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', '*'].map(m => ({ value: m, label: m }));

  return (
    <Box bg="brand.900" minH="100vh" color="brand.yellow" px={8} py={10}>
      <Heading size="lg" color="brand.yellowDark" mb={6}>API Keys</Heading>

      <Box>
        <Text mb={2} fontWeight={500}>Select User:</Text>
        <Select
          value={selectedUser}
          onChange={e => setSelectedUser(e.target.value)}
          maxW={320}
          bg="brand.800"
          color="brand.yellow"
          _focus={{ bg: 'black', color: 'brand.yellow' }}
        >
          <option value="">-- Select User --</option>
          {usernames.map(u => <option key={u} value={u}>{u}</option>)}
        </Select>
      </Box>

      {deleteError && (
        <Flex align="center" gap={2} color="red.400" mt={4} p={3} border="1px solid" borderColor="red.400" borderRadius="md" maxW="fit-content">
          <Text>{deleteError}</Text>
          <CloseButton size="sm" onClick={() => setDeleteError('')} />
        </Flex>
      )}

      {loading ? (
        <Flex mt={16} justify="center">
          <Spinner color="brand.yellow" size="xl" />
        </Flex>
      ) : selectedUser && groupedKeys.length === 0 ? (
        <Box mt={8}>No API keys for this user.</Box>
      ) : selectedUser ? (
        <Table
          variant="simple"
          colorScheme="yellow"
          mt={8}
          bg="yellow.100"
          borderRadius="md"
          color="black"
          border="1px solid #FFD600"
          sx={{
            'th, td': { border: '1px solid #FFD600' },
            th: { color: '#FFD600', background: '#000', fontWeight: 700, fontSize: 'lg', textTransform: 'uppercase' },
            td: { color: '#000', background: '#FFF9C4', fontWeight: 500, fontSize: 'xl' },
          }}
        >
          <Thead>
            <Tr>
              <Th>Username</Th>
              <Th>Path</Th>
              <Th>Methods</Th>
              <Th>Created</Th>
              <Th>Expires</Th>
              <Th>Email</Th>
              <Th>Delete</Th>
              <Th>Edit</Th>
            </Tr>
          </Thead>
          <Tbody>
            {groupedKeys.map(k => {
              const parsedApis = parseAllowedApis(k.allowedApis);
              return (
                <Tr key={k.apiKeyHash}>
                  <Td>{k.username}</Td>
                  <Td colSpan={2}>
                    <Table size="sm" variant="unstyled">
                      <Tbody>
                        {parsedApis.map((api, idx) => (
                          <Tr key={idx}>
                            <Td pl={0}>{api.path}</Td>
                            <Td>
                              {api.methods.map((m: string) => (
                                <Badge key={m} colorScheme="yellow" variant="solid" mr={1} px={2} py={1} borderRadius="md">
                                  {m}
                                </Badge>
                              ))}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Td>
                  <Td>{k.createdAt}</Td>
                  <Td>{k.expiresAt}</Td>
                  <Td>{k.emails.join(', ')}</Td>
                  <Td>
                    <Button size="sm" colorScheme="red" onClick={() => setPendingDeleteHash(k.apiKeyHash)}>Delete</Button>
                  </Td>
                  <Td>
                    <Button size="sm" colorScheme="yellow" onClick={() => handleEdit(k)}>Edit</Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Box mt={8} color="gray.500">Select a user to see their API keys.</Box>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        isOpen={!!pendingDeleteHash}
        leastDestructiveRef={cancelRef}
        onClose={() => setPendingDeleteHash(null)}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent bg="brand.800" color="brand.yellow">
          <AlertDialogHeader fontWeight="bold">Delete API Key</AlertDialogHeader>
          <AlertDialogBody>
            This will permanently delete the API key and revoke access. Are you sure?
          </AlertDialogBody>
          <AlertDialogFooter gap={3}>
            <Button ref={cancelRef} variant="ghost" color="brand.yellow" onClick={() => setPendingDeleteHash(null)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmDelete}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit modal */}
      <Modal
        isOpen={!!editingKey}
        onClose={() => setEditingKey(null)}
        isCentered
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg="brand.800" color="brand.yellow" maxW="6xl" w="92vw">
          <ModalHeader>Edit API Key for {editingKey?.username}</ModalHeader>
          <ModalBody maxH="75vh" overflowY="auto">
            <FormControl mb={4}>
              <FormLabel>Expiry Date</FormLabel>
              <Input
                type="date"
                value={editExpiry}
                onChange={e => setEditExpiry(e.target.value)}
                min={today}
                max={maxDate}
                bg="brand.800"
                color="brand.yellow"
              />
            </FormControl>

            {editRules.map((rule, index) => (
              <Box key={index} mb={4} p={4} border="1px solid #FFD600" borderRadius="md">
                <ReactSelect
                  options={apisState.map(api => ({ value: api, label: api }))}
                  value={rule.repo ? { value: rule.repo, label: rule.repo } : null}
                  onChange={opt => handleRuleChange(index, 'repo', opt ? opt.value : '')}
                  placeholder="Select API"
                  isClearable
                  isSearchable
                  styles={reactSelectStyles}
                  components={{
                    SingleValue: (props) => (
                      <div
                        style={{ userSelect: 'text', cursor: 'text', color: '#FFD600' }}
                        onMouseDown={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                      >
                        {props.data.label}
                      </div>
                    ),
                  }}
                />

                <Box mt={2}>
                  <CreatableSelect
                    isMulti
                    placeholder="Subpaths — press Enter to add, leave blank for /*"
                    value={rule.subPath ? rule.subPath.split(',').map(sp => ({ value: sp.trim(), label: sp.trim() })) : []}
                    onChange={opts =>
                      handleRuleChange(index, 'subPath', (opts as OptionType[]).map(o => o.value).join(', '))
                    }
                    onBlur={e => {
                      const input = (e.target as HTMLInputElement).value.trim();
                      if (input) {
                        const existing = rule.subPath ? rule.subPath.split(',').map(s => s.trim()) : [];
                        if (!existing.includes(input)) {
                          handleRuleChange(index, 'subPath', [...existing, input].join(', '));
                        }
                      }
                    }}
                    styles={reactSelectStyles}
                  />
                </Box>

                <Box mt={2}>
                  <ReactSelect
                    options={methodOptions}
                    value={rule.methods.map(m => ({ value: m, label: m }))}
                    isMulti
                    placeholder="Select methods"
                    onChange={opts => handleRuleChange(index, 'methods', (opts as OptionType[]).map(o => o.value))}
                    styles={reactSelectStyles}
                  />
                </Box>

                <Button size="sm" mt={3} colorScheme="red" variant="outline" onClick={() => removeRule(index)}>
                  Remove
                </Button>
              </Box>
            ))}

            <Button size="sm" mt={2} colorScheme="yellow" variant="outline" onClick={addRule}>
              + Add API
            </Button>

            {editError && <Text color="red.400" mt={3}>{editError}</Text>}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="yellow" mr={3} onClick={handleEditSave} isLoading={editSaving}>
              Save
            </Button>
            <Button onClick={() => setEditingKey(null)} variant="ghost" color="brand.yellow">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
